import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core';

import { AppCustomTable } from '@vyce/core/src/components';
import { AppButton } from '@vyce/core/src/components/AppButton';
import { getDRCEventsRequest } from '@vyce/core/src/api/legend/drc';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { DeviceContext } from '@vyce/core/src/contexts';
import { MAIN_CONTAINER_ID } from '@vyce/core/src/constants';
import { AppCustomTableColumn, DRCEvent } from '@vyce/core/src/types';
import { getUKFormattedDate } from '@vyce/core/src/utils/dates';

import { useStyles } from '../styles';

interface Props {
  open: boolean;
  userId?: string;
  handleClose: () => void;
}

const PAGE_SIZE = 25;

/** `end_user_confirmed` → `End user confirmed`, without a label table to keep in sync. */
const humanise = (eventType: string) => {
  const spaced = eventType.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const formatValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

/**
 * Every DRC decision, newest first — including the ones the platform made itself, which carry
 * no actor. This is a separately paginated feed rather than the employee's change log, so it
 * does not reuse ChangeHistoryTable.
 */
export const DRCEventsDialog: React.FC<Props> = ({ open, userId, handleClose }) => {
  const classes = useStyles();
  const { isMobile } = useContext(DeviceContext);
  const { handleServerError } = useContext(NotificationContext);
  const [events, setEvents] = useState<DRCEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const getEvents = useCallback(
    async (offset: number) => {
      if (!userId) return;
      try {
        setLoading(true);
        const { data } = await getDRCEventsRequest(userId, { offset, limit: PAGE_SIZE });
        setTotal(data.total);
        setEvents(current => (offset === 0 ? data.items : [...current, ...data.items]));
      } catch (e) {
        handleServerError(e);
      } finally {
        setLoading(false);
      }
    },
    [userId, handleServerError]
  );

  useEffect(() => {
    if (open) {
      setEvents([]);
      getEvents(0);
    }
  }, [open, getEvents]);

  const columns: AppCustomTableColumn[] = useMemo(
    () => [
      {
        name: 'created_at',
        title: 'When',
        flex: 1,
        width: '130px',
        valueGetter: (row: DRCEvent) => getUKFormattedDate(row.created_at),
      },
      {
        name: 'event_type',
        title: 'Decision',
        flex: 1.2,
        width: '160px',
        valueGetter: (row: DRCEvent) => humanise(row.event_type),
      },
      {
        name: 'actor_name',
        title: 'By who?',
        flex: 1,
        width: '150px',
        // The name is frozen into the row as it stood on the day — printed as given and never
        // re-resolved from actor_id, or an old decision starts showing a new name.
        valueGetter: (row: DRCEvent) =>
          row.actor_type === 'service' ? 'Vyce (automatic)' : row.actor_name || row.actor_id || '—',
      },
      {
        name: 'changes',
        title: 'What changed',
        flex: 2,
        width: '220px',
        renderCell: (row: DRCEvent) => {
          const entries = Object.entries(row.changes || {});
          // A re-affirmed ruling writes an event with nothing in it, on purpose.
          if (!entries.length) {
            return <Typography className={classes.hint}>Re-affirmed, no change</Typography>;
          }
          return (
            <Box display="flex" flexDirection="column">
              {entries.map(([field, change]) => (
                <Typography className={classes.hint} key={field}>
                  {`${field.replace(/_/g, ' ')}: ${formatValue(change.old)} → ${formatValue(change.new)}`}
                </Typography>
              ))}
            </Box>
          );
        },
      },
      {
        name: 'note',
        title: 'Note',
        flex: 1.4,
        width: '160px',
        valueGetter: (row: DRCEvent) => row.note || '—',
      },
    ],
    [classes.hint]
  );

  return (
    <Dialog
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      container={document.getElementById(MAIN_CONTAINER_ID)}
      open={open}
      onClose={handleClose}>
      <DialogTitle>Reverse charge history</DialogTitle>

      <DialogContent>
        <Box paddingTop={1}>
          {events.length ? (
            <AppCustomTable columns={columns} data={events} />
          ) : (
            <Typography variant="body2" color="textSecondary">
              {loading ? 'Loading…' : 'No decisions recorded yet.'}
            </Typography>
          )}

          {events.length < total && (
            <Box marginTop={2} display="flex" justifyContent="center">
              <AppButton
                variant="outlined"
                color="primary"
                size="small"
                loading={loading}
                onClick={() => getEvents(events.length)}>
                Show more
              </AppButton>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button style={{ width: 106 }} size="large" variant="outlined" onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
